import { Navigate, Route, Routes, useLocation } from "@solidjs/router";
import { TRPCClientError } from "@trpc/client";
import { Component, ErrorBoundary, lazy } from "solid-js";
import { NotFound } from "~/components/errors";
import "./App.scss";
import "./styles/index.css";

import { routeData as dashboardData } from "./routes/dashboard";
import { routeData as draftInvoiceRouteData } from "./routes/dashboard/issuers/[issuerId]/drafts/[invoiceType]/[draftInvoiceId]";
import { routeData as clientRouteData } from "./routes/dashboard/clients/[clientId]/edit";
import Home from "./routes/home";
const RegisterIssuerPage = lazy(
  () => import("./routes/dashboard/issuers/register")
);
const EditIssuerPage = lazy(
  () => import("./routes/dashboard/issuers/[issuerId]/edit")
);

const ResetPasswordPage = lazy(() => import("./routes/users/reset-password"));
const Dashboard = lazy(() => import("./routes/dashboard"));
const DashboardShell = lazy(() => import("./components/DashboardShell"));
const IssuerDashboard = lazy(
  () => import("./routes/dashboard/issuers/[issuerId]")
);
const InvoiceSettings = lazy(
  () => import("./routes/dashboard/issuers/[issuerId]/drafts/settings")
);
const IssuerDraftInvoices = lazy(
  () => import("./routes/dashboard/issuers/[issuerId]/drafts")
);
const TaxSeriesListPage = lazy(
  () => import("./routes/dashboard/issuers/[issuerId]/tax-series/index")
)
const RegisterTaxSeriesPage = lazy(
  () => import("./routes/dashboard/issuers/[issuerId]/tax-series/register")
);
const EditTaxSeriesPage = lazy(
  () => import("./routes/dashboard/issuers/[issuerId]/tax-series/[taxSeriesId]/index")
);
const ClientList = lazy(() => import("./routes/dashboard/clients"));
const RegisterClientPage = lazy(
  () => import("./routes/dashboard/clients/register")
);
const EditClientPage = lazy(
  () => import("./routes/dashboard/clients/[clientId]/edit")
);

const DraftInvoice = lazy(
  () =>
    import(
      "./routes/dashboard/issuers/[issuerId]/drafts/[invoiceType]/[draftInvoiceId]"
    )
);
const InvoiceData = lazy(() => import("./routes/invoices/[invoiceId]/index"));
const InvoiceList = lazy(() => import("./components/InvoiceList"));
const IssuerInvoiceList = lazy(() => import("./components/IssuerInvoiceList"));
const ProductList = lazy(() => import("./components/ProductList"));
const ProductForm = lazy(() => import("./components/ProductForm"));
const InvoiceResend = lazy(() => import("./routes/invoices/resend"));
const SignIn = lazy(() => import("./routes/users/sign-in"));
const SignOut = lazy(() => import("./routes/users/sign-out"));
const SignUp = lazy(() => import("./routes/users/sign-up"));
const UserProfilePage = lazy(() =>
  import("./routes/dashboard/users/[userId]/profile")
);

export const App: Component = () => {
  const location = useLocation();
  const onError = (error: unknown, reset: () => void) => {
    console.debug("boundary error", { error });
    if (error instanceof TRPCClientError) {
      console.debug({ trpcError: error });
      if (error.data?.code === "UNAUTHORIZED") {
        return (
          <Navigate href={`/users/sign-in?redirect=${location.pathname}`} />
        );
      }
    }
    return (
      <pre>
        {String(error)}
        {(error as { stack: string }).stack}
      </pre>
    );
  };
  return (
    <>
      <ErrorBoundary fallback={onError}>
        <Routes>
          <Route path="/" component={Home} />
          <Route
            path="/dashboard"
            data={dashboardData}
            component={DashboardShell}
          >
            <Route path="/" component={Dashboard} />
            <Route path="/issuers/register" component={RegisterIssuerPage} />
            <Route path="/issuers/:issuerId/edit"
              component={EditIssuerPage}
              data={dashboardData}
            />
            <Route path="/issuers/:issuerId" component={IssuerDashboard} >
            </Route>
            <Route
              path="/issuers/:issuerId/tax-series"
              component={TaxSeriesListPage}
            />
            <Route
              path="/issuers/:issuerId/tax-series/register"
              component={RegisterTaxSeriesPage}
            />
            <Route
              path="/issuers/:issuerId/tax-series/:taxSeriesId"
              component={EditTaxSeriesPage}
            />
            <Route
              path="/issuers/:issuerId/drafts/settings"
              component={InvoiceSettings}
            />
            <Route
              path="/issuers/:issuerId/drafts"
              component={IssuerDraftInvoices}
            />
            <Route
              path="/issuers/:issuerId/drafts/:invoiceType/:draftInvoiceId"
              data={draftInvoiceRouteData}
              component={DraftInvoice}
            />
            <Route path="/issuers/:issuerId/products" component={ProductList}></Route>
            <Route
              path="/issuers/:issuerId/products/add"
              component={ProductForm}
              data={dashboardData}></Route>
            <Route path="/clients" component={ClientList} />
            <Route path="/clients/register" component={RegisterClientPage} />
            <Route
              path="/clients/:clientId/edit"
              data={clientRouteData}
              component={EditClientPage}
            />
            <Route path="/issuerInvoices" component={IssuerInvoiceList} />
            <Route
              path="/users/:userId/profile"
              component={UserProfilePage}></Route>
          </Route>
          <Route path="/invoices" component={InvoiceList} />
          <Route
            path="/failed/invoices/resend"
            component={InvoiceResend}/>
          <Route path="/invoices/:invoiceId" component={InvoiceData} />
          <Route path="/users/sign-in" component={SignIn} />
          <Route path="/users/sign-out" component={SignOut} />
          <Route path="/users/reset-password" component={ResetPasswordPage} />
          <Route path="/users/sign-up" component={SignUp} />
          <Route path="/*all" component={NotFound} />
        </Routes>
      </ErrorBoundary>
    </>
  );
};
